import React, { useState, useEffect } from "react";
import { Box, Button, Container, LinearProgress, Paper, Typography } from "@mui/material";
import bkstApi from "../../api";
import moment from "moment";
import currency from "currency.js";
import { Link as RouterLink, useParams } from "react-router-dom";
import UserAvatar from "../../common/UserAvatar";
import PmCodeIcon from "../component/PmCodeIcon";
import { pmCodeLabel } from "../component/pmCodeLabel";
import CardBrand from "../component/CardBrand";

function normalizeTransactions(raw) {
  if (raw == null) return [];
  const list = Array.isArray(raw) ? raw : [raw];
  return list.sort((a, b) => Number(b.ts) - Number(a.ts));
}

function formatAmount(amount) {
  if (amount == null || amount === "") return "—";
  const n = Number(amount);
  if (Number.isNaN(n)) return String(amount);
  return currency(n).format();
}

function formatPaymentMethod(tx) {
  if (tx.pmCode) {
    return pmCodeLabel(tx.pmCode);
  }
  return "—";
}

export default function OrderTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const { orderId } = useParams();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    document.title = "Order Transactions";
    bkstApi(`/my/order/${orderId}/transaction`)
      .then((res) => {
        if (!cancelled) {
          setTransactions(normalizeTransactions(res.data.transaction));
        }
      })
      .catch(() => {
        if (!cancelled) setTransactions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h1" gutterBottom>
        Transactions
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Payment activity for this order
      </Typography>

      {transactions.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">No transactions found.</Typography>
        </Paper>
      ) : (
        <Paper variant="outlined">
          {transactions.map((tx, idx) => {
            return (
              <Box
                key={tx.id ?? idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  px: 2,
                  py: 1.5,
                  borderBottom: idx < transactions.length - 1 ? "1px solid #ececec" : "none",
                }}
              >
                <Box sx={{ flexShrink: 0, width: { xs: 96, sm: 108 } }}>
                  <Typography variant="overline" display="block" noWrap>
                    {moment(Number(tx.ts)).format("MMM D, YYYY")}
                  </Typography>
                  <Typography variant="overline" display="block" noWrap>
                    {moment(Number(tx.ts)).format("h:mm A")}
                  </Typography>
                </Box>
                <Box sx={{ flexShrink: 0, width: 72 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {formatAmount(tx.amount)}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                    {tx.pmCode && (
                      <PmCodeIcon
                        pmCode={tx.pmCode}
                        sx={{ color: "text.secondary", fontSize: 18, mt: 0.25, flexShrink: 0 }}
                      />
                    )}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" display="block" noWrap>
                        {formatPaymentMethod(tx)}
                      </Typography>
                      {tx.reference && (
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                          {tx.reference}
                        </Typography>
                      )}
                      {(tx.brand || tx.last4) && (
                        <Box sx={{ mt: 0.25 }}>
                          <CardBrand brand={tx.brand} last4={tx.last4} variant="caption" />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
                {tx.uid && (
                  <Box sx={{ flexShrink: 0 }}>
                    <UserAvatar uid={tx.uid} />
                  </Box>
                )}
              </Box>
            );
          })}
        </Paper>
      )}
      <Box mt={3}>
        <Button
          fullWidth
          size="large"
          color="secondary"
          variant="outlined"
          component={RouterLink}
          to={`/order/${orderId}`}
        >
          View Order
        </Button>
      </Box>
    </Container>
  );
}
